import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { Plus, Edit, Trash2, Search, Cpu } from 'lucide-react';

const Machines = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [formData, setFormData] = useState({ name: '', machine_type: '', location: '', capacity_per_hour: 0 });

  useEffect(() => { fetchData(); }, []);
  const fetchData = async () => { try { const r = await api.get('/production/machines'); setItems(r.data); } catch(e){} finally { setLoading(false); } };

  const openCreate = () => { setEditing(null); setFormData({ name: '', machine_type: '', location: '', capacity_per_hour: 0 }); setShowModal(true); };
  const openEdit = (m) => { setEditing(m); setFormData({ name: m.name, machine_type: m.machine_type || '', location: m.location || '', capacity_per_hour: m.capacity_per_hour || 0 }); setShowModal(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/production/machines/${editing.id}`, formData);
      else await api.post('/production/machines', formData);
      setShowModal(false); fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete machine "${name}"?`)) return;
    try { await api.delete(`/production/machines/${id}`); fetchData(); } catch(e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const getStatusColor = (s) => {
    const map = { running: 'badge-success', idle: 'badge-warning', down: 'badge-danger', maintenance: 'badge-info' };
    return `badge ${map[s] || 'badge-info'}`;
  };

  const filtered = items.filter(m => {
    const matchSearch = m.name.toLowerCase().includes(searchTerm.toLowerCase()) || (m.machine_code || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = statusFilter === 'all' || m.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div data-testid="machines-page">
      <div className="flex items-center justify-between mb-4">
        <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Machine Master</h1>
        <button onClick={openCreate} className="btn btn-primary" data-testid="add-machine-btn"><Plus size={16} /> Add Machine</button>
      </div>
      <div className="card mb-4">
        <div className="flex gap-4" style={{ flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: '200px' }}>
            <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
            <input type="text" className="form-input" placeholder="Search machines..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ paddingLeft: '40px' }} data-testid="search-input" />
          </div>
          <select className="form-select" style={{ width: '160px' }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="status-filter">
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>
      <div className="card">
        {loading ? <div>Loading...</div> : (
          <div className="table-container">
            <table>
              <thead><tr><th>Code</th><th>Name</th><th>Type</th><th>Location</th><th>Capacity/Hr</th><th>Live Status</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {filtered.length === 0 ? <tr><td colSpan={8} style={{ textAlign: 'center', padding: '20px', color: 'var(--text-tertiary)' }}>No machines found</td></tr> : filtered.map(m => (
                  <tr key={m.id} data-testid={`machine-row-${m.machine_code}`}>
                    <td className="font-mono">{m.machine_code}</td>
                    <td style={{ fontWeight: 500 }}>{m.name}</td>
                    <td>{m.machine_type || '-'}</td>
                    <td>{m.location || '-'}</td>
                    <td className="tabular-nums">{m.capacity_per_hour}</td>
                    <td><span className={getStatusColor(m.current_status)}>{m.current_status || 'idle'}</span></td>
                    <td><span className={`badge ${m.status === 'active' ? 'badge-success' : 'badge-danger'}`}>{m.status}</span></td>
                    <td>
                      <div className="flex gap-2">
                        <button onClick={() => openEdit(m)} className="btn btn-ghost btn-sm" data-testid={`edit-btn-${m.machine_code}`}><Edit size={14} /></button>
                        <button onClick={() => handleDelete(m.id, m.name)} className="btn btn-ghost btn-sm" data-testid={`delete-btn-${m.machine_code}`}><Trash2 size={14} color="#DC2626" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {showModal && (
        <div className="modal-overlay" data-testid="machine-modal">
          <div className="modal-content" style={{ maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>{editing ? 'Edit Machine' : 'Add Machine'}</h2></div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group"><label className="form-label">Name *</label><input type="text" className="form-input" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required data-testid="name-input" /></div>
                <div className="grid grid-cols-2">
                  <div className="form-group"><label className="form-label">Machine Type</label><input type="text" className="form-input" value={formData.machine_type} onChange={(e) => setFormData({...formData, machine_type: e.target.value})} placeholder="e.g. CNC, Injection Moulding" data-testid="type-input" /></div>
                  <div className="form-group"><label className="form-label">Location</label><input type="text" className="form-input" value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})} data-testid="location-input" /></div>
                </div>
                <div className="form-group"><label className="form-label">Capacity per Hour</label><input type="number" className="form-input" value={formData.capacity_per_hour} onChange={(e) => setFormData({...formData, capacity_per_hour: parseInt(e.target.value) || 0})} data-testid="capacity-input" /></div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-machine-btn">{editing ? 'Update' : 'Add Machine'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Machines;
